import { useState } from 'react'
import { DateTime } from 'luxon'
import { v4 as uuidv4 } from 'uuid'
import { DEFAULT_FOLDER_ID, EMPTY_MONGO_OBJECT_ID, TASK_PRIORITIES } from '../../constants'
import useOverviewContext from '../../context/OverviewContextProvider'
import useSelectionContext from '../../context/SelectionContextProvider'
import { usePreviewMode, useToast } from '../../hooks'
import useIsReadOnly from '../../hooks/useIsReadOnly'
import { useGetFolders } from '../../services/api/folders.hooks'
import {
    useCreateTask,
    useGetTasksV4,
    useMarkTaskDoneOrDeleted,
    useModifyTask,
    useReorderTask,
} from '../../services/api/tasks.hooks'
import { icons } from '../../styles/images'
import { TTaskFolder, TTaskV4 } from '../../utils/types'
import GTDatePicker from '../molecules/GTDatePicker'
import ScheduleTaskModal from '../molecules/ScheduleTaskModal'
import RecurringTaskTemplateModal from '../molecules/recurring-tasks/RecurringTaskTemplateModal'
import { toast } from '../molecules/toast'
import GTContextMenu from './GTContextMenu'
import { GTMenuItem } from './RadixUIConstants'
import getReorderMenuItems, { ReorderActions } from './reorderMenuItems'

export const getDeleteLabel = (task: TTaskV4) => {
    if (task.is_deleted) {
        return 'Restore task'
    }
    return 'Delete task'
}

const getMoveFolderMenuItem = (
    task: TTaskV4,
    folders: TTaskFolder[],
    onFolderClick: (folderId: string) => void
): GTMenuItem => {
    return {
        label: 'Move to folder',
        icon: icons.folder,
        subItems: [
            ...folders
                .filter((f) => !f.is_done && !f.is_trash)
                .map((f) => ({
                    label: f.name,
                    icon: f.id === DEFAULT_FOLDER_ID ? icons.inbox : icons.folder,
                    selected: f.id === task.id_folder,
                    onClick: () => onFolderClick(f.id),
                })),
        ],
    }
}

// Dragging onto the calendar is the only other way to schedule, and it never fires on touch.
export const getScheduleMenuItem = (onClick: () => void): GTMenuItem => ({
    label: 'Schedule on calendar',
    icon: icons.calendar_star,
    onClick,
})

const getSetDueDateMenuItem = (task: TTaskV4, setDate: (date: string) => void): GTMenuItem => {
    return {
        label: 'Set due date',
        icon: icons.clock,
        subItems: [
            {
                label: 'Calendar',
                renderer: () => (
                    <GTDatePicker initialDate={DateTime.fromISO(task.due_date)} setDate={setDate} onlyCalendar />
                ),
            },
        ],
    }
}

const getSetPriorityMenuItem = (task: TTaskV4, setPriority: (priority: number) => void): GTMenuItem => {
    return {
        label: 'Set priority',
        icon: icons.priority,
        subItems: TASK_PRIORITIES.map((priority, val) => ({
            label: priority.label,
            icon: priority.icon,
            selected: val === task.priority_normalized,
            iconColor: priority.color,
            onClick: () => setPriority(val),
        })),
    }
}

interface TaskContextMenuProps {
    task: TTaskV4
    children: React.ReactNode
    onOpenChange: (open: boolean) => void
    // supplied only where the surrounding list has a manual order; reordering is otherwise drag-only
    reorder?: ReorderActions
    // Starts an optional row exit and reports whether the cache update should wait for it.
    onDeleteStart?: () => boolean
}
const TaskContextMenuWrapper = ({ task, children, onOpenChange, reorder, onDeleteStart }: TaskContextMenuProps) => {
    const { data: allTasks } = useGetTasksV4(false)
    const { data: folders } = useGetFolders(false)
    const { mutate: createTask } = useCreateTask()
    const { mutate: reorderTask, mutateAsync: reorderTaskAsync } = useReorderTask(false)
    const { mutate: modifyTask } = useModifyTask(true)
    const { mutate: markTaskDoneOrDeleted } = useMarkTaskDoneOrDeleted(false)
    const [isRecurringTaskTemplateModalOpen, setIsRecurringTaskTemplateModalOpen] = useState(false)
    const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false)
    const { inMultiSelectMode, selectedTaskIds, clearSelectedTaskIds } = useSelectionContext()
    const { startTaskExits } = useOverviewContext()
    const oldToast = useToast()
    const { isPreviewMode } = usePreviewMode()
    const isReadOnly = useIsReadOnly()

    const parentTask = allTasks?.find((t) => t.id === task.id_parent)

    const showRecurringTaskOption =
        task.source?.name === 'General Task' && // must be a native task
        (!task.recurring_task_template_id || task.recurring_task_template_id === EMPTY_MONGO_OBJECT_ID) && // and not already be a recurring task
        !parentTask

    const onSingleSelectFolderClick = (folderId: string) => {
        reorderTask(
            {
                id: task.id,
                dropSectionId: folderId,
                dragSectionId: task.id_folder,
                orderingId: 1,
            },
            task.optimisticId
        )
    }
    const onMultiSelectFolderClick = (folderId: string) => {
        clearSelectedTaskIds()
        Promise.all(
            selectedTaskIds.map((id) =>
                reorderTaskAsync({
                    id,
                    dropSectionId: folderId,
                    dragSectionId: task.id_folder,
                    orderingId: 1,
                })
            )
        )
    }
    const onSingleSetDueDateClick = (date: string) => {
        modifyTask({ id: task.id, dueDate: date }, task.optimisticId)
    }
    const onMultiSetDueDateClick = (date: string) => {
        Promise.all(selectedTaskIds.map((id) => modifyTask({ id, dueDate: date })))
    }
    const onMultiSetPriorityClick = (priority: number) => {
        clearSelectedTaskIds()
        Promise.all(selectedTaskIds.map((id) => modifyTask({ id, priorityNormalized: priority })))
    }
    const onSingleDeleteClick = () => {
        markTaskDoneOrDeleted(
            {
                id: task.id,
                isDeleted: !task.is_deleted,
                waitForAnimation: !task.is_deleted && onDeleteStart?.(),
            },
            task.optimisticId
        )
    }
    const onMultiDeleteClick = () => {
        const waitForAnimation = !task.is_deleted && startTaskExits(selectedTaskIds)
        clearSelectedTaskIds()
        markTaskDoneOrDeleted({ ids: selectedTaskIds, isDeleted: !task.is_deleted, waitForAnimation })
    }

    const getPriorityOption = (task: TTaskV4): GTMenuItem => {
        return {
            label: 'Set priority',
            icon: icons.priority,
            subItems: TASK_PRIORITIES.map((priority, val) => ({
                label: priority.label,
                icon: priority.icon,
                selected: val === task.priority_normalized,
                iconColor: priority.color,
                onClick: () => {
                    if (parentTask && task) {
                        modifyTask({ id: parentTask.id, priorityNormalized: val }, task.optimisticId)
                    } else {
                        modifyTask({ id: task.id, priorityNormalized: val }, task.optimisticId)
                    }
                },
            })),
        }
    }

    const getDeleteMenuItem = (): GTMenuItem => {
        if (inMultiSelectMode) {
            return {
                label: getDeleteLabel(task),
                icon: icons.trash,
                iconColor: 'red',
                textColor: 'red',
                onClick: onMultiDeleteClick,
            }
        }
        return {
            label: getDeleteLabel(task),
            icon: icons.trash,
            iconColor: 'red',
            textColor: 'red',
            onClick: onSingleDeleteClick,
        }
    }

    const contextMenuItems: GTMenuItem[] = [
        ...(reorder ? getReorderMenuItems(reorder) : []),
        ...(task.id_folder && folders ? [getMoveFolderMenuItem(task, folders, onSingleSelectFolderClick)] : []),
        ...(!task.is_deleted && !task.is_done ? [getScheduleMenuItem(() => setIsScheduleModalOpen(true))] : []),
        getSetDueDateMenuItem(task, onSingleSetDueDateClick),
        getPriorityOption(task),
        ...(!task.id_parent && !task.is_deleted && !task.is_done
            ? [
                  {
                      label: 'Duplicate task',
                      icon: icons.clone,
                      onClick: () => {
                          const optimisticId = uuidv4()
                          const body = task.body
                          createTask({
                              title: `${task.title} (copy)`,
                              body,
                              id_folder: task.id_folder,
                              id_parent: task.id_parent,
                              optimisticId,
                          })
                          modifyTask(
                              {
                                  id: optimisticId,
                                  priorityNormalized: Math.round(task.priority_normalized) || undefined,
                                  dueDate: DateTime.fromISO(task.due_date).toISO() || undefined,
                                  recurringTaskTemplateId: task.recurring_task_template_id || undefined,
                              },
                              optimisticId
                          )
                          reorderTask(
                              {
                                  id: optimisticId,
                                  dropSectionId: task.id_folder || DEFAULT_FOLDER_ID,
                                  orderingId: task.id_ordering + 2,
                              },
                              optimisticId
                          )
                          const folderName = folders?.find((f) => f.id === task.id_folder)?.name ?? 'Task Inbox'
                          if (isPreviewMode) {
                              toast(`Task duplicated in folder ${folderName}`)
                          } else {
                              oldToast.show({ message: `Task duplicated in folder ${folderName}` })
                          }
                      },
                  },
              ]
            : []),
        ...(showRecurringTaskOption
            ? [
                  {
                      label: 'Create a recurring task',
                      icon: icons.arrows_repeat,
                      onClick: () => setIsRecurringTaskTemplateModalOpen(true),
                  },
              ]
            : []),
        getDeleteMenuItem(),
    ]

    const multiSelectContextMenuItems: GTMenuItem[] = [
        ...(task.id_folder && folders ? [getMoveFolderMenuItem(task, folders, onMultiSelectFolderClick)] : []),
        getSetDueDateMenuItem(task, onMultiSetDueDateClick),
        getSetPriorityMenuItem(task, onMultiSetPriorityClick),
        getDeleteMenuItem(),
    ]
    const visibleItems = inMultiSelectMode && !task.id_parent ? multiSelectContextMenuItems : contextMenuItems
    const readOnlyItems = isReadOnly ? visibleItems.map((item) => ({ ...item, disabled: true })) : visibleItems
    return (
        <>
            <GTContextMenu items={readOnlyItems} trigger={children} onOpenChange={onOpenChange} />
            {isRecurringTaskTemplateModalOpen && (
                <RecurringTaskTemplateModal
                    initialTask={task}
                    initialFolderId={task.id_folder}
                    onClose={() => setIsRecurringTaskTemplateModalOpen(false)}
                />
            )}
            {isScheduleModalOpen && <ScheduleTaskModal task={task} onClose={() => setIsScheduleModalOpen(false)} />}
        </>
    )
}

export default TaskContextMenuWrapper
